/**
* @license
* 
* (C) Copyright IBM Corp. 2017, 2025. All Rights Reserved.
* 
* Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except
* in compliance with the License. You may obtain a copy of the License at
* 
* http://www.apache.org/licenses/LICENSE-2.0
* 
* Unless required by applicable law or agreed to in writing, software distributed under the License
* is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
* or implied. See the License for the specific language governing permissions and limitations under
* the License.
* 
* @carbon/ai-chat 0.3.2
* 
* Built: Jun 11 2025 5:48 pm +00:00
* 
* 
*/

import { _ as __decorate, c as carbonElement } from './customElement.js';
import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { i as isEqual, c as consoleWarn, A as AppContainerExport } from './AppContainer.js';
import React__default from 'react';
import { createRoot } from 'react-dom/client';
import 'react-intl';
import '@carbon/icons-react';
import '@carbon/react';
import 'react-redux';
import '@carbon/web-components/es-custom/components/button/index.js';
import '@carbon/web-components/es-custom/components/overflow-menu/index.js';
import 'lit/directives/unsafe-html.js';
import '@carbon/web-components/es-custom/components/data-table/index.js';
import '@carbon/web-components/es-custom/components/checkbox/index.js';
import '@carbon/icon-helpers';
import '@carbon/icons';
import 'lit/directives/unsafe-svg.js';
import 'lit-html/directives/repeat.js';
import '@carbon/web-components/es-custom/components/pagination/index.js';
import '@carbon/web-components/es-custom/components/select/index.js';
import '@carbon/web-components/es-custom/components/data-table/table-skeleton.js';
import '@carbon/web-components/es-custom/components/slug/index.js';
import '@carbon/web-components/es-custom/components/ai-label/defs.js';
import '@carbon/web-components/es-custom/components/popover/defs.js';
import '@carbon/web-components/es-custom/components/skeleton-icon/index.js';
import 'react-dom';
import '@carbon/web-components/es-custom/components/ai-label/ai-label-action-button.js';
import '@carbon/web-components/es-custom/components/ai-label/ai-label.js';
import '@carbon/web-components/es-custom/components/inline-loading/index.js';
import '@carbon/web-components/es-custom/components/textarea/index.js';
import '@carbon/web-components/es-custom/components/icon-button/index.js';
import '@carbon/web-components/es-custom/components/tag/index.js';
import '@carbon/web-components/es-custom/components/chat-button/index.js';
import '@carbon/web-components/es-custom/components/button/button.js';
import '@carbon/web-components/es-custom/components/layer/index.js';

let ChatContainerInternal = class ChatContainerInternal extends LitElement {
    firstUpdated() {
        // Render the React component with any updated properties if necessary
        const style = document.createElement('style');
        style.textContent = `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        ${this.config?.element ? '' : 'z-index: var(--cds-chat-BASE-z-index);'}
      }
    `;
        this.appendChild(style);
        if (this.config) {
            this.renderReactApp();
        }
    }
    updated(changedProperties) {
        // Render the React component with any updated properties if necessary. isEqual performs a deep check, but for
        // elements only checks the reference.
        if (changedProperties.has('config') && !isEqual(this.config, changedProperties.get('config'))) {
            if (changedProperties.get('config')) {
                consoleWarn('The config object you have passed to AI chat has updated. Tearing down and re-starting the chat.');
            }
            if (this.config) {
                this.renderReactApp();
            }
        }
    }
    async renderReactApp() {
        const previousContainer = this.shadowRoot.querySelector('.cds--aichat-react-app');
        previousContainer?.remove();
        const container = document.createElement('div');
        container.classList.add('cds--aichat-react-app');
        this.shadowRoot.appendChild(container);
        // Make sure we only have one root.
        if (this.root) {
            this.root.unmount();
        }
        this.root = createRoot(container);
        this.root.render(React__default.createElement(AppContainerExport, { config: this.config, renderUserDefinedResponse: this.renderUserDefinedResponse, renderWriteableElements: this.renderWriteableElements, onBeforeRender: this.onBeforeRender, onAfterRender: this.onAfterRender, container: container }));
    }
};
__decorate([
    property({ type: Object })
], ChatContainerInternal.prototype, "config", void 0);
__decorate([
    property()
], ChatContainerInternal.prototype, "onBeforeRender", void 0);
__decorate([
    property()
], ChatContainerInternal.prototype, "onAfterRender", void 0);
__decorate([
    property()
], ChatContainerInternal.prototype, "renderUserDefinedResponse", void 0);
__decorate([
    property({ type: Object })
], ChatContainerInternal.prototype, "renderWriteableElements", void 0);
ChatContainerInternal = __decorate([
    carbonElement('cds-aichat-internal')
], ChatContainerInternal);

/**
 * The cds-aichat-container managing creating slotted elements for user_defined responses and writable elements.
 * It then passes that slotted content into cds-aichat-internal. That component will boot up the full chat application
 * and pass the slotted elements into their slots.
 */
let ChatContainer = class ChatContainer extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The existing array of slot names for all user_defined components.
         */
        this._userDefinedSlotNames = [];
        /**
         * The existing array of slot names for all writeable elements.
         */
        this._writeableElementSlots = [];
        /**
         * Adds the slot attribute to the element for the user_defined response type and then injects it into the component by
         * updating this._userDefinedSlotNames;
         */
        this.userDefinedHandler = (event) => {
            // This element already has `slot` as an attribute.
            const { slot } = event.data;
            if (!this._userDefinedSlotNames.includes(slot)) {
                this._userDefinedSlotNames = [...this._userDefinedSlotNames, slot];
            }
        };
        this.onBeforeRenderOverride = async (instance) => {
            this._instance = instance;
            this._instance.on({ type: "userDefinedResponse" /* BusEventType.USER_DEFINED_RESPONSE */, handler: this.userDefinedHandler });
            this._instance.on({ type: "chunk:userDefinedResponse" /* BusEventType.CHUNK_USER_DEFINED_RESPONSE */, handler: this.userDefinedHandler });
            this.addWriteableElementSlots();
            await this.onBeforeRender?.(instance);
        };
    }
    addWriteableElementSlots() {
        const writeableElementSlots = [this._instance.customPanels.getPanel().hostElement.getAttribute('slot')];
        Object.keys(this._instance.writeableElements).forEach(writeableElementKey => {
            writeableElementSlots.push(writeableElementKey);
        });
        this._writeableElementSlots = writeableElementSlots;
    }
    /**
     * Renders the template while passing in class functionality
     */
    render() {
        return html `<cds-aichat-internal
      .config=${this.config}
      .onAfterRender=${this.onAfterRender}
      .onBeforeRender=${this.onBeforeRenderOverride}
    >
      ${this._writeableElementSlots.map(slot => html `<div slot=${slot}><slot name=${slot}></slot></div>`)}
      ${this._userDefinedSlotNames.map(slot => html `<div slot=${slot}><slot name=${slot}></slot></div>`)}
    </cds-aichat-internal>`;
    }
};
__decorate([
    property({ type: Object })
], ChatContainer.prototype, "config", void 0);
__decorate([
    property()
], ChatContainer.prototype, "onBeforeRender", void 0);
__decorate([
    property()
], ChatContainer.prototype, "onAfterRender", void 0);
__decorate([
    state()
], ChatContainer.prototype, "_userDefinedSlotNames", void 0);
__decorate([
    state()
], ChatContainer.prototype, "_writeableElementSlots", void 0);
__decorate([
    state()
], ChatContainer.prototype, "_instance", void 0);
ChatContainer = __decorate([
    carbonElement('cds-aichat-container')
], ChatContainer);
var ChatContainer$1 = ChatContainer;

export { ChatContainer$1 as default };
